import {
  // Development & AI (8 icons)
  Code2,
  Terminal,
  Bot,
  Brain,
  Sparkles,
  Database,
  GitBranch,
  Bug,

  // Organization (5 icons)
  FolderKanban,
  Folder,
  Files,
  Library,
  BookOpen,

  // Project types (5 icons)
  Briefcase,
  Package,
  Layers,
  AppWindow,
  Rocket,

  // Creative & Design (4 icons)
  Palette,
  Paintbrush,
  PenTool,
  Lightbulb,

  // Analytics & Tools (3 icons)
  BarChart3,
  Settings,
  Globe,
} from "lucide-react";

// Client name constants
export const CLIENT_NAMES = {
  CLI: "specstory-cli",
  VSC_EXTENSION: "specstory-vsc-extension",
} as const;

// Agent name constants
export const AGENT_NAMES = {
  CLAUDE_CODE: "Claude Code",
  CURSOR: "Cursor",
  CURSOR_CLI: "Cursor CLI",
  CURSOR_IDE: "Cursor IDE",
  GITHUB_COPILOT: "GitHub Copilot",
  VS_CODE_COPILOT_IDE: "VS Code Copilot IDE",
  CODEX_CLI: "Codex CLI",
  GEMINI_CLI: "Gemini CLI",
  AMP_CLI: "Amp CLI",
  // The CLI's droidcli provider returns Name() = "Factory Droid CLI" (pkg/providers/droidcli),
  // so sessions arrive with agentName "Factory Droid CLI". This constant must match that exact
  // string or every server-side worker that branches on AGENT_NAMES.DROID_CLI (duration, etc.)
  // misses and the token-usage provider resolver falls back to "unknown".
  DROID_CLI: "Factory Droid CLI",
  DEEPSEEK_TUI: "DeepSeek TUI",
  ANTIGRAVITY_CLI: "Antigravity CLI",
  MUSE_CODE: "Muse Code",
} as const;

// Maps raw CLI-submitted agent name strings to normalized AGENT_NAMES values
export const AGENT_NAME_ALIASES: Record<string, string> = {
  cursoride: AGENT_NAMES.CURSOR_IDE,
  copilotide: AGENT_NAMES.VS_CODE_COPILOT_IDE,
};

export function normalizeAgentName(agentName: string): string {
  return AGENT_NAME_ALIASES[agentName] ?? agentName;
}

// Agent configuration
export const AGENT_CONFIG = {
  [AGENT_NAMES.GITHUB_COPILOT]: {
    displayName: AGENT_NAMES.GITHUB_COPILOT,
    icon: "/assets/githubcopilot.svg",
    docsUrl: "https://docs.specstory.com/integrations/vscode",
  },
  [AGENT_NAMES.CURSOR]: {
    displayName: AGENT_NAMES.CURSOR,
    icon: "/assets/cursor.svg",
    docsUrl: "https://docs.specstory.com/integrations/cursor",
  },
  [AGENT_NAMES.CURSOR_CLI]: {
    displayName: AGENT_NAMES.CURSOR_CLI,
    icon: "/assets/cursor.svg",
    docsUrl: "https://docs.specstory.com/integrations/cursor-cli",
  },
  [AGENT_NAMES.CLAUDE_CODE]: {
    displayName: AGENT_NAMES.CLAUDE_CODE,
    icon: "/assets/claude.svg",
    docsUrl: "https://docs.specstory.com/integrations/claude-code",
  },
  [AGENT_NAMES.CODEX_CLI]: {
    displayName: AGENT_NAMES.CODEX_CLI,
    icon: "/assets/openai.svg",
    docsUrl: "https://docs.specstory.com/integrations/codex-cli",
  },
  [AGENT_NAMES.GEMINI_CLI]: {
    displayName: AGENT_NAMES.GEMINI_CLI,
    icon: "/assets/gemini.svg",
    docsUrl: "https://docs.specstory.com/integrations/gemini-cli",
  },
  [AGENT_NAMES.AMP_CLI]: {
    displayName: AGENT_NAMES.AMP_CLI,
    icon: "/assets/amp.svg",
    docsUrl: "https://docs.specstory.com/integrations/terminal-coding-agents",
  },
  [AGENT_NAMES.DROID_CLI]: {
    displayName: AGENT_NAMES.DROID_CLI,
    icon: "/assets/droid.svg",
    docsUrl: "https://docs.specstory.com/integrations/droid-cli",
  },
  [AGENT_NAMES.DEEPSEEK_TUI]: {
    displayName: AGENT_NAMES.DEEPSEEK_TUI,
    icon: "/assets/deepseek.svg",
    docsUrl: "https://docs.specstory.com/integrations/deepseek-tui",
  },
  // Google publishes the Antigravity mark only as PNG (antigravity.google/press), so
  // this is the one agent logo that isn't an SVG. It's the "one color" (near-black)
  // variant, not the full-color gradient one, to match the other monochrome tool
  // logos — which also means it takes logoDarkClass()'s default `dark:invert`.
  [AGENT_NAMES.ANTIGRAVITY_CLI]: {
    displayName: AGENT_NAMES.ANTIGRAVITY_CLI,
    icon: "/assets/antigravity.png",
    docsUrl: "https://docs.specstory.com/integrations/antigravity-cli",
  },
  [AGENT_NAMES.MUSE_CODE]: {
    displayName: AGENT_NAMES.MUSE_CODE,
    icon: "/assets/muse.svg",
    docsUrl: "https://docs.specstory.com/integrations/muse-cli",
  },
  default: {
    displayName: "Agent",
    icon: "/assets/code.svg",
    docsUrl: "https://docs.specstory.com/overview",
  },
} as const;

// Project icon mapping - only the 25 icons available for selection
export const PROJECT_ICON_MAP = {
  // Development & AI (8 icons)
  Code2,
  Terminal,
  Bot,
  Brain,
  Sparkles,
  Database,
  GitBranch,
  Bug,

  // Organization (5 icons)
  FolderKanban,
  Folder,
  Files,
  Library,
  BookOpen,

  // Project types (5 icons)
  Briefcase,
  Package,
  Layers,
  AppWindow,
  Rocket,

  // Creative & Design (4 icons)
  Palette,
  Paintbrush,
  PenTool,
  Lightbulb,

  // Analytics & Tools (3 icons)
  BarChart3,
  Settings,
  Globe,
} as const;

// Import and re-export project constants from shared location
import {
  AVAILABLE_PROJECT_ICONS,
  AVAILABLE_PROJECT_COLORS,
  type ProjectIcon,
  type ProjectColor,
} from "../../../shared/constants";

// Re-export for other modules
export {
  AVAILABLE_PROJECT_ICONS,
  AVAILABLE_PROJECT_COLORS,
  type ProjectIcon,
  type ProjectColor,
};

// Project data with icons and colors - mutable for demo purposes
export const PROJECT_DATA: Record<
  string,
  { icon: keyof typeof PROJECT_ICON_MAP; color: string }
> = {
  "SpecStory Cloud": { icon: "Globe", color: "blue" },
  "E-commerce API": { icon: "Code2", color: "purple" },
  "ShopMobile App": { icon: "Package", color: "orange" },
  "Analytics Engine": { icon: "Database", color: "emerald" },
  "Analytics Pipeline": { icon: "Database", color: "emerald" },
};

// Color classes for projects - 11 visually distinct options (rainbow order)
export const PROJECT_COLOR_CLASSES: Record<
  string,
  {
    bg: string;
    text: string;
    border: string;
    borderBg: string;
    checkbox?: string; // Checkbox color classes
  }
> = {
  rose: {
    bg: "bg-rose-100",
    text: "text-rose-700",
    border: "border-rose-200",
    borderBg: "bg-rose-200",
    checkbox:
      "data-[state=checked]:bg-rose-600 data-[state=checked]:border-rose-600 hover:border-rose-600 focus-visible:ring-rose-600/20",
  },
  pink: {
    bg: "bg-pink-100",
    text: "text-pink-700",
    border: "border-pink-200",
    borderBg: "bg-pink-200",
    checkbox:
      "data-[state=checked]:bg-pink-600 data-[state=checked]:border-pink-600 hover:border-pink-600 focus-visible:ring-pink-600/20",
  },
  orange: {
    bg: "bg-orange-100",
    text: "text-orange-700",
    border: "border-orange-200",
    borderBg: "bg-orange-200",
    checkbox:
      "data-[state=checked]:bg-orange-600 data-[state=checked]:border-orange-600 hover:border-orange-600 focus-visible:ring-orange-600/20",
  },
  amber: {
    bg: "bg-amber-100",
    text: "text-amber-700",
    border: "border-amber-200",
    borderBg: "bg-amber-200",
    checkbox:
      "data-[state=checked]:bg-amber-600 data-[state=checked]:border-amber-600 hover:border-amber-600 focus-visible:ring-amber-600/20",
  },
  emerald: {
    bg: "bg-emerald-100",
    text: "text-emerald-700",
    border: "border-emerald-200",
    borderBg: "bg-emerald-200",
    checkbox:
      "data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600 hover:border-emerald-600 focus-visible:ring-emerald-600/20",
  },
  cyan: {
    bg: "bg-cyan-100",
    text: "text-cyan-700",
    border: "border-cyan-200",
    borderBg: "bg-cyan-200",
    checkbox:
      "data-[state=checked]:bg-cyan-600 data-[state=checked]:border-cyan-600 hover:border-cyan-600 focus-visible:ring-cyan-600/20",
  },
  blue: {
    bg: "bg-blue-100",
    text: "text-blue-700",
    border: "border-blue-200",
    borderBg: "bg-blue-200",
    checkbox:
      "data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 hover:border-blue-600 focus-visible:ring-blue-600/20",
  },
  purple: {
    bg: "bg-purple-100",
    text: "text-purple-700",
    border: "border-purple-200",
    borderBg: "bg-purple-200",
    checkbox:
      "data-[state=checked]:bg-purple-600 data-[state=checked]:border-purple-600 hover:border-purple-600 focus-visible:ring-purple-600/20",
  },
  slate: {
    bg: "bg-slate-100",
    text: "text-slate-700",
    border: "border-slate-200",
    borderBg: "bg-slate-200",
    checkbox:
      "data-[state=checked]:bg-slate-600 data-[state=checked]:border-slate-600 hover:border-slate-600 focus-visible:ring-slate-600/20",
  },
  stone: {
    bg: "bg-stone-100",
    text: "text-stone-700",
    border: "border-stone-200",
    borderBg: "bg-stone-200",
    checkbox:
      "data-[state=checked]:bg-stone-600 data-[state=checked]:border-stone-600 hover:border-stone-600 focus-visible:ring-stone-600/20",
  },
  gray: {
    bg: "bg-gray-100",
    text: "text-gray-700",
    border: "border-gray-200",
    borderBg: "bg-gray-200",
    checkbox:
      "data-[state=checked]:bg-gray-600 data-[state=checked]:border-gray-600 hover:border-gray-600 focus-visible:ring-gray-600/20",
  },
};

// Gradient colors for connector cards
export const TOOL_GRADIENT_COLORS = {
  Cursor: "#01B9D9", // Cyan
  "GitHub Copilot": "#F6C768", // Yellow
  "Claude Code": "#EB7138", // Orange
} as const;

// Mock projects - mutable array for demo purposes
export const MOCK_PROJECTS = [
  {
    id: "specstory-cloud",
    name: "SpecStory Cloud",
    icon: "Globe",
    color: "blue",
    description: "Cloud-based conversation tracking and analytics platform",
  },
  {
    id: "auth-system",
    name: "Auth System",
    icon: "Database",
    color: "emerald",
    description: "OAuth2 authentication and authorization service",
  },
  {
    id: "api-design",
    name: "API Design",
    icon: "Code2",
    color: "purple",
    description: "RESTful API architecture and documentation",
  },
  {
    id: "mobile-app",
    name: "Mobile App",
    icon: "Package",
    color: "orange",
    description: "Cross-platform mobile application for iOS and Android",
  },
];

// Function to add a new project
export function addNewProject() {
  const index = MOCK_PROJECTS.length + 1;
  const colors = AVAILABLE_PROJECT_COLORS;

  // Use only the icons available in the customization menu
  const iconKeys = AVAILABLE_PROJECT_ICONS;

  const randomColor = colors[Math.floor(Math.random() * colors.length)];
  const randomIcon = iconKeys[Math.floor(Math.random() * iconKeys.length)];

  const newProject = {
    id: `project-${index}`,
    name: `Project ${index}`,
    icon: randomIcon,
    color: randomColor,
    description: "Auto-generated project description",
  };

  MOCK_PROJECTS.push(newProject);

  // Also add to PROJECT_DATA for consistency
  PROJECT_DATA[newProject.name] = { icon: randomIcon, color: randomColor };

  return newProject;
}

// Mock connected tools
export const MOCK_CONNECTED_TOOLS = [
  {
    name: "Cursor",
    logo: "/assets/cursor.svg",
    status: "connected",
    lastSync: "5 min ago",
    conversations: 89,
  },
  {
    name: "GitHub Copilot",
    logo: "/assets/githubcopilot.svg",
    status: "connected",
    lastSync: "12 min ago",
    conversations: 67,
  },
  {
    name: "Claude Code",
    logo: "/assets/claude.svg",
    status: "connected",
    lastSync: "1 hour ago",
    conversations: 45,
  },
];

// Mock recent conversations
export const MOCK_RECENT_PROMPTS = [
  {
    id: "conv-1",
    firstExchangeId: "exc-1",
    title: "OAuth Authentication Implementation",
    tool: "claude-code",
    toolLogo: "/assets/claude.svg",
    project: "SpecStory Cloud",
    projectId: "specstory-cloud",
    projectColor: "blue",
    projectIcon: "Globe",
    timestamp: "2 hours ago",
    preview:
      "How do I implement OAuth authentication with Google and GitHub providers?",
    isUnread: true,
    branch: "main",
  },
  {
    id: "conv-2",
    firstExchangeId: "exc-3",
    title: "Database Query Optimization",
    tool: "cursor",
    toolLogo: "/assets/cursor.svg",
    project: "E-commerce API",
    projectId: "api-design",
    projectColor: "purple",
    projectIcon: "Code2",
    timestamp: "Yesterday",
    preview:
      "Our product search query is taking 3+ seconds. How can we optimize this Prisma query?",
    isUnread: false,
    branch: "feature/search-optimization",
  },
  {
    id: "conv-3",
    firstExchangeId: "exc-5",
    title: "React Navigation Deep Linking",
    tool: "copilot",
    toolLogo: "/assets/githubcopilot.svg",
    project: "ShopMobile App",
    projectId: "mobile-app",
    projectColor: "orange",
    projectIcon: "Package",
    timestamp: "2 days ago",
    preview:
      "How do I implement deep linking with React Navigation to handle app:// URLs?",
    isUnread: false,
    branch: "feature/deep-linking",
  },
];

export const TIME_FILTERS = {
  all: "All time",
  today: "Today",
  week: "Past week",
  month: "Past month",
} as const;

export const getTimeFilterDisplay = (timeFilter: string) => {
  if (timeFilter === "") {
    return TIME_FILTERS.all;
  }
  return TIME_FILTERS[timeFilter as keyof typeof TIME_FILTERS];
};

export const EXTENSION_ID = "SpecStory.specstory-vscode";

export const EXTENSION_CALLBACK_PATH =
  "SpecStory.specstory-vscode/refresh-token";

/**
 * Get the extension marketplace URI for opening the extension in an IDE
 * Format: {ide}:extension/{extension-id} (lowercase extension ID)
 * @param ideType - "cursor" or "vscode"
 * @returns The extension URI (e.g., "cursor:extension/specstory.specstory-vscode")
 */
export function getExtensionMarketplaceUri(
  ideType: "cursor" | "vscode",
): string {
  const extensionIdLowercase = EXTENSION_ID.toLowerCase();
  return `${ideType}:extension/${extensionIdLowercase}`;
}

export const EXTENSION_REDIRECT_INFO = {
  cursor: {
    url: "cursor",
    name: "Cursor",
    logo: "/assets/cursor.svg",
    bgColor: "bg-black",
    logoClasses: "brightness-0 invert",
  },
  vscode: {
    url: "vscode",
    name: "VS Code",
    logo: "/assets/vscode.svg",
    bgColor: "bg-transparent",
    logoClasses: "",
  },
  "vscode-insiders": {
    url: "vscode-insiders",
    name: "VS Code Insiders",
    logo: "/assets/vscode.svg",
    bgColor: "bg-transparent",
    logoClasses: "",
  },
};
